import React from "react";
import {
  Card,
  Row,
  Col,
  Switch,
  Slider,
  Button,
  Statistic,
  Select,
  InputNumber,
} from "antd";
import { AimOutlined, EnvironmentOutlined } from "@ant-design/icons";
import MomentDatePicker from "../MomentDatePicker";

const { Option } = Select;
const { RangePicker } = MomentDatePicker;

export default function QuoteSidePanel({
  statusFilter,
  useStatusFilter,
  dateRange,
  useDateRange,
  subtotalMin,
  useSubtotalFilter,
  useProximityFilter,
  proximityRange,
  userLocation,
  customLocation,
  useCustomLocation,
  isSelectingLocation,
  loadingLocation,
  filteredQuotes,
  stats,
  handleStatusChange,
  handleDateRangeChange,
  handleSubtotalMinChange,
  handleProximityRangeChange,
  setUseStatusFilter,
  setUseDateRange,
  setUseSubtotalFilter,
  setUseProximityFilter,
  useMyLocation,
  startCustomLocationSelection,
  filtering,
  showCompetitors,
  setShowCompetitors,
  showPartners,
  setShowPartners,
}) {
  // Format currency helper
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  return (
    <div>
      {/* Statistiche */}
      <Card
        title="Statistiche"
        size="small"
        style={{ marginBottom: 16 }}
        loading={filtering}
      >
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Statistic
              title="Totale preventivi"
              value={stats.all}
              valueStyle={{ color: "#3D5864" }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Valore totale filtrato"
              value={formatCurrency(stats.totalAmount || 0)}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col span={24}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Statistic
                title="Bozze"
                value={stats.bozza || 0}
                valueStyle={{ color: "grey", fontSize: "14px" }}
                style={{ marginRight: "8px" }}
              />
              <Statistic
                title="Inviati"
                value={stats.inviato || 0}
                valueStyle={{ color: "blue", fontSize: "14px" }}
                style={{ marginRight: "8px" }}
              />
              <Statistic
                title="Accettati"
                value={stats.accettato || 0}
                valueStyle={{ color: "orange", fontSize: "14px" }}
                style={{ marginRight: "8px" }}
              />
              <Statistic
                title="Pagati"
                value={stats.pagato || 0}
                valueStyle={{ color: "green", fontSize: "14px" }}
                style={{ marginRight: "8px" }}
              />
              <Statistic
                title="Rifiutati"
                value={stats.rifiutato || 0}
                valueStyle={{ color: "red", fontSize: "14px" }}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Filtri */}
      <Card title="Filtri" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          {/* Stato preventivo */}
          <Col span={24}>
            <div style={{ marginBottom: 8 }}>
              <span>Stato preventivo</span>
            </div>
            <Select
              value={statusFilter}
              onChange={handleStatusChange}
              style={{ width: "100%" }}
            >
              <Option value="tutti">Tutti</Option>
              <Option value="bozza">Bozza</Option>
              <Option value="inviato">Inviato</Option>
              <Option value="accettato">Accettato</Option>
              <Option value="pagato">Pagato</Option>
              <Option value="rifiutato">Rifiutato</Option>
            </Select>
          </Col>

          {/* Data */}
          <Col span={24}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ marginRight: 8 }}>Periodo</span>
              <Switch
                checked={useDateRange}
                onChange={(checked) => setUseDateRange(checked)}
                size="small"
              />
            </div>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              disabled={!useDateRange}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </Col>

          {/* Subtotal */}
          <Col span={24}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ marginRight: 8 }}>Importo minimo</span>
              <Switch
                checked={useSubtotalFilter}
                onChange={(checked) => setUseSubtotalFilter(checked)}
                size="small"
              />
            </div>
            <InputNumber
              value={subtotalMin}
              onChange={handleSubtotalMinChange}
              disabled={!useSubtotalFilter}
              style={{ width: "100%" }}
              min={0}
              formatter={(value) =>
                `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/€\s?|(,*)/g, "")}
            />
          </Col>
        </Row>
      </Card>

      {/* Posizione */}
      <Card title="Posizione" size="small">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ marginRight: 8 }}>Raggio di ricerca</span>
              <Switch
                checked={useProximityFilter}
                onChange={(checked) => setUseProximityFilter(checked)}
                disabled={!userLocation && !customLocation}
                size="small"
              />
            </div>
            <Slider
              min={1}
              max={200}
              onChange={handleProximityRangeChange}
              value={proximityRange}
              disabled={!useProximityFilter}
              tooltip={{ formatter: (value) => `${value} km` }}
            />
          </Col>
          <Col span={24}>
            <Button
              type="primary"
              icon={<AimOutlined />}
              onClick={useMyLocation}
              loading={loadingLocation}
              block
              style={{ marginBottom: 8 }}
              ghost
            >
              Usa la mia posizione
            </Button>
            <Button
              type={useCustomLocation ? "primary" : "default"}
              icon={<EnvironmentOutlined />}
              onClick={startCustomLocationSelection}
              loading={isSelectingLocation}
              block
              ghost={useCustomLocation}
            >
              {isSelectingLocation
                ? "Clicca sulla mappa..."
                : customLocation
                ? "Modifica posizione personalizzata"
                : "Seleziona posizione sulla mappa"}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Toggles for Competitors and Partners */}
      <Card title="Visualizzazioni" size="small" style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 'bold' }}>Mostra Competitor:</span>
              <Switch 
                checked={showCompetitors}
                onChange={setShowCompetitors}
                disabled={filtering}
                checkedChildren="Sì"
                unCheckedChildren="No"
              />
            </div>
          </Col>
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 'bold' }}>Mostra Partner:</span>
              <Switch 
                checked={showPartners}
                onChange={setShowPartners}
                disabled={filtering}
                checkedChildren="Sì"
                unCheckedChildren="No"
              />
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
